import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Tooltip, IconButton } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { useState, useEffect } from 'react';
import { subscribe } from '../../services/utils/pubsub';
import authService from '../../services/AuthService';
import UpcomingDrawer from '../../CalendarWidget/UI/UpcomingDrawer';
import { tooltipSx } from './jernyTooltipStyling';

const J = {
    muted: '#a3d1ff',
    accent: '#66b2ff',
    border: '#294766',
};

/**
 * Minimal sticky footer for content pages — copyright + legal links only.
 */
const STATUS_LABEL = {
    connected: 'Connected',
    disconnected: 'Disconnected',
    connecting: 'Connecting...',
};

const STATUS_COLOR = {
    connected: '#4caf7d',
    disconnected: '#555',
    connecting: '#a3d1ff',
};

export default function MiniFooter() {
    const [status, setStatus] = useState('connecting');
    const [opener, setOpener] = useState(null);

    useEffect(() => {
        if (authService.isConnected()) setStatus('connected');
        const unsubError = subscribe('SystemSocketError', () => setStatus('disconnected'));
        const unsubConnected = subscribe('SystemSocketConnected', () => setStatus('connected'));
        return () => {
            unsubError();
            unsubConnected();
        };
    }, []);

    /**
     * Toggles the upcoming events drawer — passes this button itself as opener,
     * or null to close. Local UI state, no engine/pub-sub involved.
     * @param {React.MouseEvent} e
     */
    const handleEventsClick = (e) => {
        const target = e.currentTarget;
        setOpener(opener ? null : target);
    };

    /**
     * Closes the upcoming events drawer.
     */
    const handleEventsClose = () => {
        setOpener(null);
    };

    return (
        <Box sx={{ position: 'sticky', bottom: 0, bgcolor: '#0d0d1a', zIndex: 10 }}>
            <Divider sx={{ borderColor: J.border }} />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 24,
                    px: 2,
                }}
            >
                {/* Left — Terms & Privacy links */}
                <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                    <Link
                        variant="caption"
                        href="/terms"
                        sx={{ color: J.muted, opacity: 0.6, textDecoration: 'none', '&:hover': { color: J.accent, opacity: 1 } }}
                    >
                        Terms
                    </Link>
                    <Link
                        variant="caption"
                        href="/privacy"
                        sx={{ color: J.muted, opacity: 0.6, textDecoration: 'none', '&:hover': { color: J.accent, opacity: 1 } }}
                    >
                        Privacy
                    </Link>
                </Box>

                {/* Center — copyright */}
                <Typography variant="caption" sx={{ color: J.muted, opacity: 0.6 }}>
                    © {new Date().getFullYear()} Jerny — jerny.nl
                </Typography>

                {/* Right — status text + dot + calendar opener */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLOR[status], flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: J.muted, opacity: 0.7, fontSize: '0.65rem' }}>
                            {STATUS_LABEL[status]}
                        </Typography>
                    </Box>
                    <Tooltip title="Show upcoming event list" componentsProps={tooltipSx}>
                        <IconButton
                            id="calendar-anchor"
                            size="small"
                            onClick={handleEventsClick}
                            sx={{ color: J.muted, p: '2px', '&:hover': { color: J.accent } }}
                        >
                            <EventIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <UpcomingDrawer opener={opener} onClose={handleEventsClose} />
        </Box>
    );
}
